import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import type { Place } from '../../types/place';
import { Link, useParams } from 'react-router-dom';
import { useAppSelector } from '../../redux/hooks';
import { selectCurrentLanguage, selectMainPlace } from '../../redux/selectors';
import { useTranslation } from 'react-i18next';
import { getChildren, createImageUrl } from '../../services/data';
import { getLangClass } from '../../services/langUtils';
import roundPlay from '../../assets/roundPlay.svg';
import styles from './TodExhibit.module.css';
import { palette } from '../../themes/theme';

interface TodExhibitCardProps {
  place: Place;
  langClass: string;
  slug: string;
}

const TodExhibitCard: React.FC<TodExhibitCardProps> = ({ place, langClass, slug }) => {
  const title = place.translations?.[0]?.information_title || place.information_title || '';
  const description = place.translations?.[0]?.information_short_description
    || place.translations?.[0]?.information_description || '';
  const imageUrl = createImageUrl(place.information_photo);

  return (
    <Link to={`/${slug}/${place.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
      <div className={styles['tod-exhibit']} style={{ cursor: 'pointer' }}>
        <div className={styles['tod-exhibit-image']}>
          <img src={imageUrl} alt={title} />
          <img src={roundPlay} className={styles['tod-play-overlay']} alt="play" />
        </div>
        <div className={styles['tod-exhibit-body']}>
          <p className={`${styles['tod-exhibit-name']} ${langClass}`}>{title}</p>
          {description && (
            <p className={`${styles['tod-exhibit-description']} ${langClass}`}>{description}</p>
          )}
        </div>
      </div>
    </Link>
  );
};

interface TodExhibitListProps {
  place: Place;
  childrenIds: (number | string)[];
}

const TodExhibitList: React.FC<TodExhibitListProps> = ({ place, childrenIds }) => {
  const { i18n } = useTranslation();
  const { slug } = useParams();
  const currentLanguage = useAppSelector(selectCurrentLanguage);
  const mainPlace = useAppSelector(selectMainPlace);
  const [children, setChildren] = useState<Place[]>([]);
  const langClass = getLangClass(currentLanguage);

  const galleryTitle = place?.translations?.[0]?.information_title || '';
  const galleryOrder = place?.parent_list_order ?? place?.sort ?? 0;
  const terminologyLabel = mainPlace?.translations?.[0]?.information_subtitle || 'Gallery';

  useEffect(() => {
    const fetchChildren = async () => {
      if (!childrenIds || childrenIds.length === 0) return;
      const result = await getChildren('Place', currentLanguage ?? 'en', childrenIds);
      setChildren(result || []);
    };
    fetchChildren();
  }, [childrenIds, currentLanguage]);

  return (
    <div className={styles['tod-exhibits-container']} style={{ direction: i18n.dir() }}>
      <div className={`${styles['tod-exhibits-header']} ${langClass}`}>
        <h1>{`${terminologyLabel} ${galleryOrder}`}</h1>
        <p>{galleryTitle}</p>
      </div>
      <Box component="hr" sx={{
        display: 'block',
        backgroundColor: palette.primary.accent,
        color: palette.primary.accent,
        width: '100%',
        border: 0,
        borderTop: `1px solid ${palette.primary.accent}`,
        margin: '2rem 0',
      }} />
      <ul className={`${styles['tod-exhibit-list']} ${langClass}`}>
        {children.map((child, index) => (
          <li key={child.id || index}>
            <TodExhibitCard place={child} langClass={langClass} slug={slug ?? ''} />
          </li>
        ))}
      </ul>
    </div>
  );
};

export default TodExhibitList;
